import React from "react";
import { FaEye, FaWallet } from "react-icons/fa";

import {
  BORDER,
  DANGER,
  GREEN,
  MUTED,
  PAYMENT_LABELS,
  PAYMENT_TYPE_OPTIONS,
  formatDateTime,
  formatMoney,
  formatPaymentMode,
  getFileName,
  renderValue,
} from "./reservationViewUtils";

const SummaryRow = ({ label, value }) => (
  <div className="d-flex justify-content-between align-items-center py-2">
    <span style={{ color: MUTED }}>{label}</span>
    <span style={{ fontWeight: 500 }}>{value}</span>
  </div>
);

const ReceiptCell = ({ receiptUrl }) => {
  if (!receiptUrl) return "-";

  return (
    <div className="d-flex align-items-center gap-2">
      <span style={{ color: MUTED }}>{getFileName(receiptUrl)}</span>
      <a
        href={receiptUrl}
        target="_blank"
        rel="noreferrer"
        title="View receipt"
        style={{ color: GREEN, lineHeight: 1 }}
      >
        <FaEye />
      </a>
    </div>
  );
};

const ReservationPaymentsPanel = ({
  payments,
  settlement,
  money,
  draft,
  onDraftChange,
  onReceiptChange,
  receiptName,
  editingType,
  onEdit,
  onSave,
  onCancelEdit,
  hideAdvance = false,
  saving = false,
}) => {
  const rows = ["advance", "installment", "final"]
    .map((key) => ({ key, ...payments[key] }))
    .filter((row) => Number(row.amount || 0) > 0);

  const settlementAmount = Number(settlement?.amount || 0);
  const isEditing = Boolean(editingType);
  const remaining = money.remainingBalance;

  // Advance is captured at booking time, so it is no longer offered after check-in.
  const typeOptions = hideAdvance
    ? PAYMENT_TYPE_OPTIONS.filter((option) => option.value !== "advance")
    : PAYMENT_TYPE_OPTIONS;

  const existingReceiptUrl =
    editingType === "settlement"
      ? settlement?.receiptUrl
      : payments[editingType || draft.type]?.receiptUrl;

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex align-items-center gap-2 mb-3">
          <FaWallet style={{ color: GREEN, fontSize: 15 }} />
          <h5 className="mb-0" style={{ fontSize: 16, fontWeight: 600 }}>
            Payments
          </h5>
        </div>

        <div className="table-responsive">
          <table className="table table-striped align-middle mb-0">
            <thead>
              <tr>
                <th>Type</th>
                <th>Mode</th>
                <th>Remarks</th>
                <th>Deposit At</th>
                <th>Receipt</th>
                <th className="text-end">Amount</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length || settlementAmount > 0 ? (
                <>
                  {rows.map((row) => (
                    <tr key={row.key}>
                      <td>{PAYMENT_LABELS[row.key]}</td>
                      <td>{formatPaymentMode(row.mode)}</td>
                      <td>{renderValue(row.remarks)}</td>
                      <td>{formatDateTime(row.recordedAt)}</td>
                      <td>
                        <ReceiptCell receiptUrl={row.receiptUrl} />
                      </td>
                      <td className="text-end">{formatMoney(row.amount)}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={saving}
                          onClick={() => onEdit(row.key)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                  {settlementAmount > 0 ? (
                    <tr>
                      <td>{PAYMENT_LABELS.settlement}</td>
                      <td>{formatPaymentMode(settlement.mode)}</td>
                      <td>{renderValue(settlement.remarks)}</td>
                      <td>{formatDateTime(settlement.recordedAt)}</td>
                      <td>
                        <ReceiptCell receiptUrl={settlement.receiptUrl} />
                      </td>
                      <td className="text-end">- {formatMoney(settlementAmount)}</td>
                      <td className="text-end">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          disabled={saving}
                          onClick={() => onEdit("settlement")}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ) : null}
                </>
              ) : (
                <tr>
                  <td colSpan={7} className="text-center" style={{ color: MUTED }}>
                    No payments recorded
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div
          className="mt-4"
          style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: 16 }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0" style={{ fontWeight: 600 }}>
              {isEditing ? `Update ${PAYMENT_LABELS[editingType]}` : "Record a Payment"}
            </h6>
            {isEditing ? (
              <span style={{ color: MUTED, fontSize: 13 }}>
                Editing an existing payment
              </span>
            ) : null}
          </div>

          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Type</label>
              <select
                className="form-select"
                value={draft.type}
                disabled={isEditing || saving}
                onChange={(event) =>
                  onDraftChange({ ...draft, type: event.target.value })
                }
              >
                {typeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Amount</label>
              <input
                type="number"
                min="0"
                className="form-control"
                placeholder="0"
                value={draft.amount}
                disabled={saving}
                onChange={(event) =>
                  onDraftChange({ ...draft, amount: event.target.value })
                }
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Mode</label>
              <select
                className="form-select"
                value={draft.mode || "cash"}
                disabled={saving}
                onChange={(event) =>
                  onDraftChange({ ...draft, mode: event.target.value })
                }
              >
                <option value="cash">Cash</option>
                <option value="bank transfer">Bank Transfer</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Deposit At</label>
              <input
                type="datetime-local"
                className="form-control"
                value={draft.recordedAt || ""}
                disabled={saving}
                onChange={(event) =>
                  onDraftChange({ ...draft, recordedAt: event.target.value })
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Receipt</label>
              <input
                type="file"
                accept="image/*,.pdf"
                className="form-control"
                disabled={saving}
                onChange={(event) => onReceiptChange(event.target.files?.[0] || null)}
              />
              {receiptName || existingReceiptUrl ? (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  {receiptName ? (
                    <span style={{ color: MUTED }}>{receiptName}</span>
                  ) : (
                    <a
                      href={existingReceiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: GREEN }}
                    >
                      View existing
                    </a>
                  )}
                </div>
              ) : null}
            </div>
            <div className="col-md-8">
              <label className="form-label">Remarks</label>
              <input
                type="text"
                className="form-control"
                placeholder="Optional"
                value={draft.remarks || ""}
                disabled={saving}
                onChange={(event) =>
                  onDraftChange({ ...draft, remarks: event.target.value })
                }
              />
            </div>
            <div className="col-12 d-flex gap-2">
              <button
                type="button"
                className="btn btn-secondary flex-grow-1"
                disabled={saving}
                onClick={onSave}
              >
                {saving ? "Saving..." : isEditing ? "Update Payment" : "Add Payment"}
              </button>
              {isEditing ? (
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  disabled={saving}
                  onClick={onCancelEdit}
                >
                  Cancel
                </button>
              ) : null}
            </div>
          </div>
        </div>

        {settlementAmount > 0 ? (
          <hr style={{ borderTop: `1px solid ${BORDER}`, opacity: 1, margin: "22px 0" }} />
        ) : null}
        {/* <SummaryRow label="Total Amount" value={formatMoney(money.amountCharged)} /> */}
        {/* <SummaryRow label="Collected Amount" value={formatMoney(money.collected)} /> */}
        {settlementAmount > 0 ? (
          <SummaryRow
            label={PAYMENT_LABELS.settlement}
            value={`- ${formatMoney(settlementAmount)}`}
          />
        ) : null}
        {/* <div className="d-flex justify-content-between align-items-center py-2">
          <span style={{ fontWeight: 600 }}>Remaining</span>
          <span
            style={{
              fontWeight: 700,
              fontSize: 17,
              color: remaining === 0 ? GREEN : DANGER,
            }}
          >
            {formatMoney(Math.abs(remaining))}
            {remaining < 0 ? " (Refund)" : ""}
          </span>
        </div> */}
      </div>
    </div>
  );
};

export default ReservationPaymentsPanel;
